import Service, { inject as service } from '@ember/service';

import { ErrorSeverity } from 'later/services/errors';
import { fetch } from 'later/utils/fetch';
import { STRATEGY } from 'shared/utils/retry';

import type UserAgentService from 'ember-useragent/services/user-agent';
import type ErrorsService from 'later/services/errors';
import type { SnakeCasedProperties } from 'type-fest';

const SOCIAL_AUTH_EVENTS_ENDPOINT = '/api/v2/social_auth_events';
const CLIENT_STAGES = ['started', 'callback', 'failed'] as const;
// Android, Facebook, and Instagram add explicit in-app browser tokens. An iOS WebView uses AppleWebKit without Safari.
const IN_APP_WEBVIEW_PATTERN =
  /(?:;\s*wv\)|\bFBAN\/|\bFBAV\/|\bFB_IAB\/|\bInstagram(?:\s|\/)|(?:iPhone|iPad|iPod).*AppleWebKit(?!.*Safari))/i;

export type SocialAuthEventFlow = 'connect' | 'reconnect';
export type SocialAuthEventFlowId = `${string}-${string}-${string}-${string}-${string}`;
export type SocialAuthEventProvider =
  | 'facebook'
  | 'google_youtube'
  | 'instagram'
  | 'linkedin'
  | 'mavely'
  | 'meta_marketing_api'
  | 'pinterest'
  | 'snapchat'
  | 'threads'
  | 'tiktok'
  | 'tiktok_business'
  | 'twitter'
  | 'youtube';
export type SocialAuthEventStage = (typeof CLIENT_STAGES)[number];
export type SocialAuthEventReason = 'access_denied' | 'scope_declined' | 'sdk_blocked' | 'popup_blocked';
export type SocialAuthEventSource = 'ember_redirect' | 'ember_popup' | 'ember_webview';

export interface SocialAuthEvent {
  provider: SocialAuthEventProvider;
  flow: SocialAuthEventFlow;
  stage: SocialAuthEventStage;
  flowId: SocialAuthEventFlowId;
  reason?: SocialAuthEventReason;
  errorClass?: string;
  errorMessage?: string;
  eventName?: string;
  groupId?: string;
  socialProfileId?: string;
  isPopup?: boolean;
}

type SocialAuthEventRequest = SnakeCasedProperties<Omit<SocialAuthEvent, 'isPopup'>> & {
  source: SocialAuthEventSource;
};

export default class SocialAuthEventsService extends Service {
  @service declare errors: ErrorsService;
  @service declare userAgent: UserAgentService;

  mintFlowId(): SocialAuthEventFlowId {
    return crypto.randomUUID();
  }

  async emit(event: SocialAuthEvent): Promise<void> {
    if (!CLIENT_STAGES.includes(event.stage)) {
      return;
    }

    try {
      await fetch(
        SOCIAL_AUTH_EVENTS_ENDPOINT,
        {
          method: 'POST',
          keepalive: true,
          body: this.#requestBody(event)
        },
        { intl: null, numRetries: 0, retryStrategy: STRATEGY.DEFAULT, raw: false }
      );
    } catch (error) {
      this.errors.log(
        'Failed to emit social auth event',
        { error: error instanceof Error ? error.message : String(error) },
        ErrorSeverity.Debug
      );
    }
  }

  #requestBody(event: SocialAuthEvent): SocialAuthEventRequest {
    const request: SocialAuthEventRequest = {
      provider: event.provider,
      flow: event.flow,
      stage: event.stage,
      flow_id: event.flowId,
      source: this.#source(event.isPopup)
    };

    if (event.reason) {
      request.reason = event.reason;
    }
    if (event.errorClass) {
      request.error_class = event.errorClass;
    }
    if (event.errorMessage) {
      request.error_message = event.errorMessage;
    }
    if (event.eventName) {
      request.event_name = event.eventName;
    }
    if (event.groupId) {
      request.group_id = event.groupId;
    }
    if (event.socialProfileId) {
      request.social_profile_id = event.socialProfileId;
    }

    return request;
  }

  #source(isPopup = false): SocialAuthEventSource {
    // Webview identifies the host browser, so it takes precedence when that browser also opens an OAuth popup.
    if (IN_APP_WEBVIEW_PATTERN.test(this.userAgent.userAgent)) {
      return 'ember_webview';
    }

    return isPopup ? 'ember_popup' : 'ember_redirect';
  }
}

declare module '@ember/service' {
  interface Registry {
    'social/auth-events': SocialAuthEventsService;
  }
}
